import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { useState, useCallback } from 'react'
import { WixIntegration, WixProfile, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useUpdateExternalIntegration } from 'hooks/queries/externalIntegrations/useUpdateExternalIntegration'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { WixAuthorSelect } from 'components/BlogMetadata/components/integrations/WixAuthorSelect/WixAuthorSelect'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import Styles from '../IntegrationSuccessModal/IntegrationSuccessModal.module.scss'
import { Heading } from 'almanac-ui/Heading/Heading'
import { AutopilotInfo } from 'components/IntegrationSuccessModal/AutopilotInfo'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { WixMember } from 'api/clients/BlogMetadataRecordsClient'

interface Props {
  integration: WixIntegration
  onClose: () => void
  onPressOutside?: () => void
}

export const WixConnectedSuccessModal = ({ integration, onClose, onPressOutside }: Props) => {
  const { closeModal } = useModals()
  const { mutateAsync: updateExternalIntegration, isLoading } = useUpdateExternalIntegration()
  const { data: brandPlan } = useCurrentBrandPlan()
  const { mutateAsync: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlan?.id || 0 })
  const workspace = useCurrentWorkspace()

  const profile = integration.additional_data as WixProfile
  const availableAuthors: WixMember[] = profile?.wix_members || []

  const [selectedAuthorId, setSelectedAuthorId] = useState<string | undefined>(() => {
    if (profile?.selected_author_id) {
      return profile.selected_author_id
    }
    if (availableAuthors.length === 1) {
      return availableAuthors[0].id
    }
    return undefined
  })

  const handleClose = useCallback(() => {
    closeModal()
    onClose()
  }, [closeModal, onClose])

  const handleSaveDefaults = useCallback(async () => {
    if (!selectedAuthorId) {
      showToastMessage('Please select a default author', 'error')
      return
    }

    try {
      // Update the external integration with selected author
      await updateExternalIntegration({
        id: integration.id,
        additional_data: {
          ...profile,
          selected_author_id: selectedAuthorId,
        },
      })

      // Update the brand plan to set Wix as the selected blog platform
      if (brandPlan && updateBrandPlan) {
        await updateBrandPlan({
          selected_blog_platform: ExternalIntegrationEnum.Wix,
        })
      }

      showToastMessage('Saved Successfully', 'success')
      handleClose()
    } catch (error) {
      showToastMessage('Failed to save defaults. Please try again.', 'error')
    }
  }, [selectedAuthorId, updateExternalIntegration, integration.id, profile, handleClose, brandPlan, updateBrandPlan])

  const canSave = selectedAuthorId
  const isAutopilot = workspace?.is_virtual_marketer

  return (
    <Modal.Root
      size="sm"
      onPressOutside={onPressOutside}
      onClose={handleClose}
      aria-label="Wix Account Connected Successfully"
    >
      <Modal.Header
        variant="hero"
        heroImage="https://res.cloudinary.com/almanac/image/upload/v1755871734/blaze_assets/65cfcb6ed487608453ac773ecee3a53be5e327f9_exitid.png"
        heroImageAlt=""
        title={isAutopilot ? `Your Wix content will start autoposting!` : `Your Wix account is connected!`}
        headingLevel={1}
      />
      <Modal.Content>
        <div className={Styles.content}>
          <div className={Styles.description}>
            <Text>Set your default author. You can update this anytime in Brand Kit or when editing a post.</Text>
          </div>

          <div className={Styles.fieldsContainer}>
            <div className={Styles.fieldContainer}>
              <Heading level={4}>Default author for all blog posts</Heading>
              <WixAuthorSelect
                members={availableAuthors}
                selectedAuthorId={selectedAuthorId}
                onAuthorSelect={setSelectedAuthorId}
                placeholder="Select a default author"
              />
            </div>
          </div>

          {isAutopilot && <AutopilotInfo integration={integration} onClose={handleClose} />}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton variant="primary" onPress={handleSaveDefaults} isDisabled={!canSave || isLoading}>
            {isLoading ? 'Saving...' : 'Confirm'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
